import type { CSSProperties, ReactNode } from 'react';
import { cx } from '@parrot/utils';
import { Skeleton } from '@/components/primitives';
import styles from './StatTile.module.css';

export interface StatTileFact {
  label: string;
  value: string;
}

export interface StatTileProps {
  /** Small caps label: "UCAN", "Content Catalog". */
  label: string;
  /** The headline figure or word, already formatted. */
  value: string;
  /** A line under the figure: what it covers or means. */
  sub?: ReactNode;
  /** Anything under the figure — a SplitBar, a SplitLegend. */
  children?: ReactNode;
  /** Small label / value pairs along the bottom. */
  facts?: StatTileFact[];
  /** Colour token for a rule across the top, tying the tile to a chart series. */
  accent?: string;
  /** With `onToggle`, the tile is a toggle button; selected shows gold (user-set). */
  selected?: boolean;
  onToggle?: () => void;
  /** Placeholder shapes in the tile's own layout while the figure loads. */
  loading?: boolean;
  className?: string;
}

/**
 * A headline figure in a bordered tile. Static by default; with `onToggle` it
 * becomes a toggle (e.g. a region tile that scopes the page).
 */
export function StatTile({
  label,
  value,
  sub,
  children,
  facts,
  accent,
  selected = false,
  onToggle,
  loading = false,
  className,
}: StatTileProps) {
  if (loading) {
    return (
      <div className={cx(styles.tile, className)} aria-busy="true">
        <span className={styles.label}>{label}</span>
        <Skeleton width="45%" height={38} />
        <Skeleton shape="text" width="70%" />
        <Skeleton height={6} />
      </div>
    );
  }
  const body = (
    <>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{value}</span>
      {sub && <span className={styles.sub}>{sub}</span>}
      {children}
      {facts && facts.length > 0 && (
        <span className={styles.facts}>
          {facts.map((f) => (
            <span key={f.label}>
              <span className={styles.factLabel}>{f.label}</span> {f.value}
            </span>
          ))}
        </span>
      )}
    </>
  );
  const props = {
    className: cx(styles.tile, accent && styles.accented, className),
    style: accent ? ({ '--accent': accent } as CSSProperties) : undefined,
  };

  return onToggle ? (
    <button
      type="button"
      {...props}
      className={cx(props.className, styles.toggle)}
      data-selected={selected || undefined}
      aria-pressed={selected}
      onClick={onToggle}
    >
      {body}
    </button>
  ) : (
    <div {...props}>{body}</div>
  );
}
